/**
 * Chat Interface Component
 * Handles natural language query input and displays conversation history
 */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { translateQuery, getExampleQueries, conversationService } from '../services/nlpService';
import { isWaterTenant, isEmissionsTenant } from '../config/gisConfig';
import { executeGISQuery, findNearbyTracts, findNearbyTractsMultiCenter } from '../services/gisService';
import dossierService from '../services/dossierService';
import { Send, Loader, AlertCircle, Lightbulb, Save, FolderOpen, Download, Upload, Trash2, X, Sparkles, ArrowRight } from 'lucide-react';
import { useCurrentRole, canDo } from './RoleSwitcher';

const APP_TITLE = isEmissionsTenant
  ? 'Nexus Emissions'
  : isWaterTenant
  ? 'Nexus Water'
  : 'TPL GIS Query Assistant';
const APP_SUBTITLE = isEmissionsTenant
  ? 'Ask about leaks, fenceline events, dispatch — in plain English.'
  : isWaterTenant
  ? 'Ask questions about your water assets using natural language.'
  : 'Ask questions about your land, minerals, and well data in natural language.';

// Feature ID prefixes that can be clicked from chat tables to fly the map.
const FLY_ID_PREFIX_LAYER = {
  LK: 'leaks', DM: 'distribution_mains', TM: 'transmission_mains',
  SL: 'service_lines', WO: 'work_orders_open', PR: 'pipe_risk', RT: 'survey_routes'
};

function findFlyableIDs(text) {
  if (!text) return [];
  const out = [];
  const reA = /\b(LK|DM|TM|SL|WO|PR|RT)-\d{3,5}\b/g;
  const reB = /\bSENSOR-[A-D]\d{1,2}\b/g;
  const reC = /\b(C-\d{2,3}|CREW-[A-Z]+)\b/g;
  let m;
  while ((m = reA.exec(text))) out.push({ start: m.index, end: m.index + m[0].length, text: m[0], layer: FLY_ID_PREFIX_LAYER[m[1]] });
  while ((m = reB.exec(text))) out.push({ start: m.index, end: m.index + m[0].length, text: m[0], layer: 'sensors' });
  while ((m = reC.exec(text))) out.push({ start: m.index, end: m.index + m[0].length, text: m[0], layer: 'crews' });
  out.sort((a, b) => a.start - b.start);
  const filtered = [];
  let lastEnd = -1;
  for (const it of out) { if (it.start >= lastEnd) { filtered.push(it); lastEnd = it.end; } }
  return filtered;
}

function renderTextWithIDs(text, key) {
  if (!text) return <span key={key}></span>;
  const matches = findFlyableIDs(text);
  if (matches.length === 0) return <span key={key}>{text}</span>;
  const out = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) out.push(<span key={`${key}-t${i}`}>{text.substring(cursor, m.start)}</span>);
    out.push(
      <button
        key={`${key}-id${i}`}
        className="nexus-fly-id"
        onClick={() => window.dispatchEvent(new CustomEvent('nexus-fly-to', { detail: { id: m.text, layer: m.layer } }))}
        title={`Click to highlight ${m.text} on the map`}
        style={{ background: 'rgba(37,99,235,0.12)', border: 'none', color: '#1d4ed8', fontWeight: 600, padding: '0 5px', borderRadius: 3, cursor: 'pointer', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 'inherit' }}
      >{m.text}</button>
    );
    cursor = m.end;
  });
  if (cursor < text.length) out.push(<span key={`${key}-end`}>{text.substring(cursor)}</span>);
  return <React.Fragment key={key}>{out}</React.Fragment>;
}

function renderBoldLine(line, lineKey) {
  const parts = [];
  let remaining = line;
  let partIdx = 0;
  while (remaining.length > 0) {
    const boldStart = remaining.indexOf('**');
    if (boldStart === -1) {
      parts.push(renderTextWithIDs(remaining, `${lineKey}-r${partIdx++}`));
      break;
    }
    if (boldStart > 0) parts.push(renderTextWithIDs(remaining.substring(0, boldStart), `${lineKey}-r${partIdx++}`));
    const boldEnd = remaining.indexOf('**', boldStart + 2);
    if (boldEnd === -1) {
      parts.push(renderTextWithIDs(remaining.substring(boldStart), `${lineKey}-r${partIdx++}`));
      break;
    }
    const boldText = remaining.substring(boldStart + 2, boldEnd);
    parts.push(<strong key={`${lineKey}-b${partIdx++}`}>{renderTextWithIDs(boldText, `${lineKey}-b${partIdx}`)}</strong>);
    remaining = remaining.substring(boldEnd + 2);
  }
  return parts;
}

function parseMarkdownTable(lines, startIdx) {
  if (startIdx + 1 >= lines.length) return null;
  const first = lines[startIdx];
  const second = lines[startIdx + 1];
  if (!first.startsWith('|') || !first.endsWith('|')) return null;
  if (!/^\|[\s\-:|]+\|$/.test(second.trim())) return null;
  const parseCells = (l) => l.slice(1, -1).split('|').map((s) => s.trim());
  const header = parseCells(first);
  const rows = [];
  let i = startIdx + 2;
  while (i < lines.length && lines[i].startsWith('|') && lines[i].endsWith('|')) {
    rows.push(parseCells(lines[i]));
    i++;
  }
  return { header, rows, endIdx: i - 1 };
}

function FormattedText({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const tbl = parseMarkdownTable(lines, i);
    if (tbl) {
      blocks.push(
        <table key={`tbl${i}`} className="chat-result-table" style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              {tbl.header.map((c, j) => (
                <th key={j} style={{ padding: '6px 10px', borderBottom: '1px solid #cbd5e1', fontWeight: 600, color: '#0f172a' }}>
                  {renderBoldLine(c, `th${i}-${j}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tbl.rows.map((r, ri) => (
              <tr key={ri} style={{ background: ri % 2 ? '#f8fafc' : 'white' }}>
                {r.map((c, ci) => (
                  <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #e2e8f0', color: '#0f172a' }}>
                    {renderBoldLine(c, `td${i}-${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      i = tbl.endIdx + 1;
      continue;
    }
    const isBullet = line.trimStart().startsWith('- ');
    const content = isBullet ? renderBoldLine(line.replace(/^[\s]*- /, ''), `bul${i}`) : renderBoldLine(line, `ln${i}`);
    blocks.push(
      isBullet
        ? <div key={`b${i}`} className="chat-bullet" style={{ paddingLeft: 14 }}>• {content}</div>
        : <div key={`l${i}`} className="chat-line">{content || <br />}</div>
    );
    i++;
  }
  return <span className="formatted-text">{blocks}</span>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatInterface = forwardRef(function ChatInterface({ onQueryResults, onError, onAnalytics, onClearAnalytics, onSymbologyChange, onScadaQuery }, ref) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [lastResults, setLastResults] = useState(null);
  const lastResultsRef = useRef(null); // Ref for immediate access (React state is async)
  const [analyticsActive, setAnalyticsActive] = useState(false);
  const [lastAnalyticsQuery, setLastAnalyticsQuery] = useState(null);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);
  const [pendingInteraction, setPendingInteraction] = useState(null); // For smart follow-up questions
  const messagesEndRef = useRef(null);
  const exampleQueries = getExampleQueries();

  // Smart interactive queries — tenant-aware. Each chip opens a follow-up
  // question with domain-relevant options.
  const smartQueriesEmissions = [
    {
      id: 'filter-district',
      label: '🏘️ Filter by District',
      question: 'Which district?',
      options: ['Jefferson', 'Northside', 'Eastgate', 'Riverside'],
      template: 'Show critical leaks in {value} District'
    },
    {
      id: 'filter-grade',
      label: '🚨 Filter by Leak Grade',
      question: 'Which grade?',
      options: ['Grade 3 (hazardous)', 'Grade 2 (probable)', 'Grade 1 (non-hazardous)'],
      template: 'List all {value} leaks'
    },
    {
      id: 'filter-material',
      label: '🛠️ Filter by Pipe Material',
      question: 'Which material?',
      options: ['Cast iron', 'Bare steel', 'Polyethylene'],
      template: 'Show {value} pipe segments'
    },
    {
      id: 'filter-risk',
      label: '📉 Filter by Risk Tier',
      question: 'Which risk tier?',
      options: ['Critical', 'High', 'Medium', 'Low'],
      template: 'Show {value} risk pipe segments'
    },
    {
      id: 'filter-survey-period',
      label: '📅 Filter by Survey Period',
      question: 'Which survey period?',
      options: ['Q3-2026', 'Q2-2026', 'Q3-2025'],
      template: 'Show leak indications from {value} survey'
    },
    {
      id: 'filter-fenceline',
      label: '🏭 Fenceline View',
      question: 'What about Crestwood Refinery?',
      options: ['Active anomalies', 'Triggered sensors', 'Most likely source', 'Process units status'],
      template: '{value} at Crestwood Refinery'
    },
    {
      id: 'filter-crew',
      label: '👷 Crew Dispatch',
      question: 'Dispatch action?',
      options: ['Build dispatch list for tomorrow', 'Show available crews', 'Crews near Jefferson District', 'Crews near Northside'],
      template: '{value}'
    },
  ];

  const smartQueriesWater = [
    { id: 'filter-county-water', label: '🏛️ Filter by County',
      question: 'Which county?',
      options: ['Reeves', 'Midland', 'Eddy', 'Ector', 'Lea', 'Ward', 'Loving'],
      template: 'Show critical alerts in {value} County' },
    { id: 'filter-asset-water', label: '🚰 Filter by Asset Type',
      question: 'Which asset type?',
      options: ['Disposal Wells', 'Water Sources', 'Transfer Pipelines', 'Treatment Facilities'],
      template: 'Show {value}' },
  ];

  const smartQueriesTpl = [
    { id: 'filter-county', label: '🏛️ Filter by County',
      question: 'Which county would you like to explore?',
      options: ['Reeves', 'Howard', 'Midland', 'Pecos', 'Loving', 'Martin', 'Culberson', 'Ward'],
      template: 'Show tracts in {value} County' },
    { id: 'filter-operator', label: '🏢 Filter by Operator',
      question: 'Which operator are you interested in?',
      options: ['Diamondback', 'EOG', 'ConocoPhillips', 'Mewbourne', 'Pioneer', 'Devon', 'Chevron', 'Occidental'],
      template: 'Show wells operated by {value}' },
    { id: 'filter-status', label: '📊 Filter by Well Status',
      question: 'What well status would you like to see?',
      options: ['Producing', 'DUC', 'Drilling', 'Permitted', 'Shut-in'],
      template: 'Show {value} wells' },
    { id: 'filter-formation', label: '⛏️ Filter by Formation',
      question: 'Which formation/interval?',
      options: ['Wolfcamp', 'Bone Spring', 'Spraberry', 'Delaware', 'Avalon'],
      template: 'Show wells in {value}' },
    { id: 'filter-acreage', label: '📏 Filter by Acreage',
      question: 'What acreage threshold?',
      options: ['Over 100 acres', 'Over 200 acres', 'Over 500 acres', 'Under 50 acres'],
      template: 'Show tracts {value}' },
    { id: 'filter-contract', label: '📝 Filter by Contract Type',
      question: 'What type of contract?',
      options: ['Lease', 'Easement', 'Surface', 'Mineral', 'ROW'],
      template: 'Show {value} contracts' },
  ];

  const smartQueries = isEmissionsTenant
    ? smartQueriesEmissions
    : isWaterTenant
    ? smartQueriesWater
    : smartQueriesTpl;

  // Saved Analysis (Dossier) state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [analysisName, setAnalysisName] = useState('');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const fileInputRef = useRef(null);

  // Expose sendQuery method to parent components via ref
  useImperativeHandle(ref, () => ({
    sendQuery: (query) => {
      console.log('📤 External query received:', query);
      handleSubmit(null, query);
    }
  }));

  // Load saved analyses on mount
  useEffect(() => {
    async function loadAnalyses() {
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
    loadAnalyses();
  }, []);

  // Track analytics data for saving
  useEffect(() => {
    // This effect captures analytics data when it changes
    // We'll use a custom event or callback approach
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if there are messages)
  useEffect(() => {
    // Don't auto-scroll on welcome screen - only scroll when there are actual messages
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-refresh analytics when lastResults changes (if analytics mode is active)
  useEffect(() => {
    async function refreshAnalytics() {
      if (analyticsActive && lastAnalyticsQuery) {
        // If results are empty, clear analytics
        if (!lastResults || lastResults.length === 0) {
          console.log('🔄 Query returned 0 results - clearing analytics');
          if (onClearAnalytics) {
            onClearAnalytics();
          }
          return;
        }

        // Otherwise, refresh analytics with new data
        console.log('🔄 Auto-refreshing analytics for updated results. Count:', lastResults.length);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(lastResults);

        // Update analytics panel with refreshed data
        if (onAnalytics) {
          onAnalytics({
            query: lastAnalyticsQuery,
            summary: summary
          });
        }
      }
    }

    refreshAnalytics();
  }, [lastResults, analyticsActive, lastAnalyticsQuery, onAnalytics, onClearAnalytics]);

  // Handle query submission
  const handleSubmit = async (e, customQuery = null) => {
    if (e) e.preventDefault();

    const query = customQuery || input;
    if (!query.trim()) return;

    setShowExamples(false);
    setInput('');
    setLoading(true);

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Check if this is a symbology/color change request
      if (conversationService.isSymbologyChangeQuery(query)) {
        const symbologyChange = conversationService.parseSymbologyChange(query);
        if (symbologyChange && onSymbologyChange) {
          console.log('🎨 Symbology change detected:', symbologyChange);
          onSymbologyChange(symbologyChange);

          // Add confirmation message
          const targetLabel = symbologyChange.target === 'wellStatus' ? 'well status' :
                              symbologyChange.target === 'tractType' ? 'tract type' :
                              symbologyChange.target === 'allWells' ? 'all wells' :
                              symbologyChange.target === 'allTracts' ? 'all tracts' : 'features';
          const confirmMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: `🎨 Updated map symbology! Changed ${targetLabel} "${symbologyChange.value}" to color ${symbologyChange.color}. The map will reflect this change.`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, confirmMessage]);
          setLoading(false);
          return;
        }
      }

      // Check if this is a filter query on existing results
      const isFilter = conversationService.isFilterQuery(query);
      const hasResults = lastResults && lastResults.length > 0;

      console.log('🔍 Filter check:', {
        query,
        isFilter,
        hasResults,
        lastResultsCount: lastResults?.length || 0
      });

      if (isFilter && hasResults) {
        console.log('✅ Filter query detected. Filtering existing results...');

        // Filter existing results
        const filteredResults = conversationService.filterResults(lastResults, query);
        console.log(`📊 Filtered ${lastResults.length} → ${filteredResults.length} results`);

        // Add assistant thinking message
        const thinkingMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: `Filtering current results based on: "${query}"`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, thinkingMessage]);

        // Display filtered results
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Filtered to ${filteredResults.length} feature${filteredResults.length !== 1 ? 's' : ''} (from ${lastResults.length} total).`,
          results: {
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Update stored results (both state and ref for immediate access)
        lastResultsRef.current = filteredResults;
        setLastResults(filteredResults);
        conversationService.storeQuery({ layer: 'tpl-tracts', description: query }, filteredResults);

        // Determine if this filter should trigger a zoom
        // Zoom when: county filter, operator filter, or significant result reduction
        const isCountyFilter = /\b(county|culberson|reeves|howard|loving|martin|midland|ector|ward|winkler|andrews|pecos|crane|glasscock|reagan|lea|eddy|chaves)\b/i.test(query);
        const isOperatorFilter = /\b(diamondback|eog|mewbourne|pioneer|chevron|exxon|bp|shell|apache|devon|occidental|ovintiv|conoco|conocophillips|coterra|marathon|cimarex|centennial|parsley|callon|energen|fasken|henry|encana|anadarko|noble|laredo|matador|highpeak|endeavor|summit|grenadier|birch)\b/i.test(query);
        const isSignificantReduction = filteredResults.length > 0 && filteredResults.length < lastResults.length * 0.8;
        const shouldZoom = isCountyFilter || isOperatorFilter || isSignificantReduction;

        // Calculate extent from filtered results for proper zooming
        let extent = null;
        if (shouldZoom && filteredResults.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          filteredResults.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            const type = feature.geometry?.type;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Pass filtered results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: filteredResults,
            count: filteredResults.length,
            layer: 'tpl-tracts',
            query: query,
            zoom: shouldZoom, // Zoom on county filter or significant reduction
            extent: extent // Include extent for proper zoom bounds
          });
        }

        // Smart Analyst: Generate insights for filtered results
        if (filteredResults.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(filteredResults, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return;
      }

      // Step 1: Translate natural language to GIS query
      let intent = await translateQuery(query, lastResults);

      // Deterministic SCADA pre-filter — if the query has SCADA keywords,
      // force layer: 'scada' regardless of what the LLM returned. Prevents
      // misclassification (e.g. "show all critical alerts" → analytics).
      // EXCEPTION: if the upstream preclassifier already routed to crews / leaks (proximity) /
      // pipe_risk (by district), respect that and don't override.
      const PRECLASSIFIED_LAYERS = ['crews', 'pipe_risk'];
      const isPreclassifiedCrewPlan = intent.layer === 'crews';
      const isPreclassifiedProximity = intent.layer === 'leaks' && intent.proximity_to;
      const SCADA_KEYWORDS = /\b(alert|alarm|scada|telemetry|offline|outage|downtime|nearest crew|eta|over[- ]?capacity|high pressure|pressure drop|pump offline|fault|recovery rate|troubleshoot|comm[- ]?(loss|degraded)|filter clog|valve fault|motor fault|what'?s wrong|status of|problem with)\b/i;
      if (SCADA_KEYWORDS.test(query) && intent.layer !== 'scada' && !isPreclassifiedCrewPlan && !isPreclassifiedProximity && !PRECLASSIFIED_LAYERS.includes(intent.layer)) {
        const lq = query.toLowerCase();
        const idMatch = query.match(/\b([A-Z]{1,3})[-\s]?(\d{2,5})\b/);
        const counties = ['reeves', 'midland', 'eddy', 'ector', 'lea', 'ward', 'loving', 'martin', 'pecos', 'winkler'];
        const county = counties.find(c => lq.includes(c));
        const filters = {};
        if (idMatch) filters.asset_id = `${idMatch[1]}-${idMatch[2].padStart(4, '0')}`;
        if (county) filters.county = county.charAt(0).toUpperCase() + county.slice(1);
        if (lq.includes('critical')) filters.severity = 'critical';
        let scadaIntent = 'list_alerts';
        if (lq.includes('crew') || lq.includes('dispatch') || lq.includes('fix') ||
            lq.includes('nearest') || lq.includes('eta')) scadaIntent = 'nearest_crew';
        else if (lq.includes('impact') || lq.includes('capacity offline')) scadaIntent = 'impact';
        else if (filters.asset_id || lq.includes("what's wrong") || lq.includes('status of')) scadaIntent = 'asset_dossier';

        intent = {
          layer: 'scada',
          scope: filters.asset_id ? 'asset' : 'region',
          filters,
          intent: scadaIntent,
          description: filters.asset_id
            ? `SCADA dossier for ${filters.asset_id}`
            : `SCADA alerts${filters.county ? ' in ' + filters.county : ''}${filters.severity ? ' (' + filters.severity + ')' : ''}`,
          confidence: 0.85,
          forcedScada: true,
        };
      }

      // Add assistant thinking message
      const thinkingMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I understand you want to: ${intent.description}`,
        intent: intent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, thinkingMessage]);

      // ─── SCADA / Operations query ────────────────────────────────────────
      if (intent.layer === 'scada') {
        try {
          let endpoint;
          let payload;
          if (intent.scope === 'asset' && intent.filters?.asset_id) {
            endpoint = `${BACKEND_URL}/api/scada/asset/${encodeURIComponent(intent.filters.asset_id)}`;
            payload = { mode: 'asset', assetId: intent.filters.asset_id, filters: intent.filters || {} };
          } else {
            const qs = new URLSearchParams();
            if (intent.filters?.county)   qs.set('county', intent.filters.county);
            if (intent.filters?.basin)    qs.set('basin', intent.filters.basin);
            if (intent.filters?.severity) qs.set('severity', intent.filters.severity);
            endpoint = `${BACKEND_URL}/api/scada/region?${qs.toString()}`;
            payload = { mode: 'region', filters: intent.filters || {} };
          }

          const res = await fetch(endpoint);
          if (!res.ok) throw new Error(`SCADA request failed (${res.status})`);
          const data = await res.json();

          // Build a chat-friendly narrative
          let narrative;
          let suggestions = [];   // Smart Analyst follow-up prompts
          const lq = (query || '').toLowerCase();
          const isEmissionsQuery = /grade|leak|methane|fenceline|sensor|pipe|district|jefferson|northside|eastgate|riverside|crestwood|refinery|crew|dispatch/i.test(query);

          if (payload.mode === 'asset') {
            const top = data.alerts?.[0];
            if (!top) {
              narrative = `**${data.asset_name || data.asset_id}** is currently nominal — no active SCADA alerts.`;
            } else {
              const crew = data.nearest_crews?.[0];
              const impactLine = data.operational_impact?.summary || 'See panel for details.';
              narrative = `**${data.asset_name}** — ${data.alerts.length} active alert${data.alerts.length > 1 ? 's' : ''}.\n\n` +
                `**Top issue:** ${top.title} _(${top.severity.toUpperCase()})_  \n${top.description}\n\n` +
                `**Operational impact:** ${impactLine}` +
                (crew
                  ? `\n\n**Recommended crew:** ${crew.name} (${crew.lead}) — ${crew.distance_miles} mi away, ETA ${crew.eta_minutes} min · ${crew.skill_match}/3 skill match`
                  : '');
              payload.alertedAssetIds = [data.asset_id];
            }
          } else {
            const sevSum = data.by_severity || {};
            const impact = data.total_impact_value || data.total_impact_bbl_day || 0;
            const unit = data.total_impact_unit || 'bbl/day';
            const districtLabel = intent.filters?.county ? ` in **${intent.filters.county}**` : '';
            const sevLabel = intent.filters?.severity ? ` (${intent.filters.severity})` : '';

            // Build a ranked list of the top alerts with full data — no blank fields (PM #5/#8)
            const topList = (data.top_alerts || []).slice(0, 5).map((a, i) => {
              const tag = a.severity === 'critical' ? '🔴' : a.severity === 'warning' ? '🟠' : '🔵';
              const ppm = a.peak_concentration_ppm || a.description || '';
              const grade = a.grade ? ` · Grade ${a.grade}` : '';
              const risk = a.risk_score ? ` · risk ${a.risk_score}/100` : '';
              const district = a.county ? ` · ${a.county}` : '';
              return `${i + 1}. ${tag} **${a.asset_name}** — ${typeof ppm === 'number' ? ppm + ' ppm CH₄' : a.title}${grade}${risk}${district}`;
            }).join('\n');

            narrative = `Found **${data.total_alerts} active alert${data.total_alerts !== 1 ? 's' : ''}**${districtLabel}${sevLabel}.\n\n` +
              `**Severity:** ${Object.entries(sevSum).map(([k, v]) => `${v} ${k}`).join(' · ') || '—'}\n\n` +
              `**Capacity at risk:** ${impact.toLocaleString()} ${unit}` +
              (topList ? `\n\n**Top ${Math.min(5, data.top_alerts.length)} by impact:**\n${topList}` : '');

            payload.alertedAssetIds = (data.top_alerts || []).map(a => a.asset_id);

            // Smart Analyst suggestions — wired to PM demo script (Picarro Q1→Q2→Q3, Q4→Q5, Q7→Q8)
            // Renderer expects { text, query } objects.
            const sug = (text, query) => ({ text, query: query || text });
            if (isEmissionsQuery) {
              const isLeakQuery = /leak|grade|jefferson|northside|eastgate|riverside/i.test(lq);
              const isFenceline = /fenceline|sensor|crestwood|refinery|methane spike|anomaly/i.test(lq);
              const isCapital  = /pipe replacement|capital|priorit/i.test(lq);

              if (isLeakQuery && !lq.includes('school') && !lq.includes('hospital')) {
                suggestions.push(sug('Which Grade 2 leaks are within 100 feet of a school or hospital?'));
                suggestions.push(sug('List all Grade 3 emergency leaks'));
              } else if (isCapital) {
                suggestions.push(sug('Create a crew dispatch plan for tomorrow'));
                suggestions.push(sug('Show the highest risk pipe segments by district'));
              } else if (isFenceline) {
                suggestions.push(sug('What is the most likely source?'));
                suggestions.push(sug('Draft the regulatory incident notification for this event'));
              } else {
                suggestions.push(sug('Build a crew dispatch plan for these alerts'));
                suggestions.push(sug('Show pipe replacement priority by district'));
              }
            }
          }

          // Replace the thinking message with the narrative
          setMessages(prev => prev.map(m =>
            m.id === thinkingMessage.id
              ? { ...m, type: 'result', content: narrative, timestamp: new Date() }
              : m,
          ));

          // Smart Analyst follow-up message
          if (suggestions.length > 0) {
            setMessages(prev => [...prev, {
              id: Date.now() + 4,
              type: 'smart-analyst',
              insights: [],
              suggestions,
              summary: 'Suggested follow-up:',
              timestamp: new Date(),
            }]);
          }

          // Open the SCADA panel via App
          if (onScadaQuery) onScadaQuery(payload);
        } catch (err) {
          setMessages(prev => prev.map(m =>
            m.id === thinkingMessage.id
              ? { ...m, type: 'error', content: `SCADA query failed: ${err.message}`, timestamp: new Date() }
              : m,
          ));
          if (onError) onError(err.message);
        } finally {
          setLoading(false);
        }
        return;
      }

      // Check if this is an analytics query
      if (intent.layer === 'analytics') {
        // Handle analytics query - don't query the map
        // Use ref for immediate access (state may not have updated yet in sequential queries)
        const currentResults = lastResultsRef.current || lastResults;
        console.log('Analytics query detected. currentResults:', currentResults?.length);
        if (!currentResults || currentResults.length === 0) {
          throw new Error('Please run a search first to analyze the results.');
        }

        // Generate analytics response
        const analyticsText = conversationService.generateAnalytics(query, currentResults);

        // Get enriched summary with contract data
        const summary = await conversationService.getEnrichedSummary(currentResults);

        const analyticsMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: analyticsText,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, analyticsMessage]);

        // Enable analytics mode and store query for auto-refresh
        setAnalyticsActive(true);
        setLastAnalyticsQuery(query);

        // Pass analytics data to parent for display in AnalyticsPanel
        if (onAnalytics) {
          console.log('ChatInterface calling onAnalytics with:', { query, summary });
          onAnalytics({
            query: query,
            summary: summary
          });
        } else {
          console.warn('onAnalytics callback not provided!');
        }

        return; // Don't execute GIS query or update map
      }

      // Check if this is a contract-based query
      if (intent.layer === 'contracts') {
        console.log('Contract query detected:', intent);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

        // Build query params from contractFilters
        const params = new URLSearchParams();
        if (intent.contractFilters.minRevenue) {
          params.append('minRevenue', intent.contractFilters.minRevenue);
        }
        if (intent.contractFilters.maxRevenue) {
          params.append('maxRevenue', intent.contractFilters.maxRevenue);
        }
        if (intent.contractFilters.status) {
          params.append('status', intent.contractFilters.status);
        }
        if (intent.contractFilters.contractType) {
          params.append('contractType', intent.contractFilters.contractType);
        }
        if (intent.contractFilters.operator) {
          params.append('operator', intent.contractFilters.operator);
        }

        // Step 1: Get tract numbers from contracts
        const contractResponse = await fetch(`${backendUrl}/api/contracts/tract-numbers?${params}`);
        if (!contractResponse.ok) {
          throw new Error('Failed to fetch contract data');
        }
        const contractData = await contractResponse.json();
        const tractNumbers = contractData.data;

        if (tractNumbers.length === 0) {
          throw new Error('No tracts found matching those contract criteria.');
        }

        console.log(`Found ${tractNumbers.length} tracts from contract query`);

        // Step 2: Query GIS layer with those tract numbers
        const gisQuery = {
          layer: 'tracts',
          conditions: [{
            field: 'TRACT_NUMBER',
            operator: 'IN',
            value: tractNumbers,
            type: 'string'
          }],
          description: intent.description,
          zoom: false
        };

        // Execute GIS query to get tract geometries
        const results = await executeGISQuery(gisQuery);

        // Store results for follow-up queries
        conversationService.storeQuery(gisQuery, results.features);

        // Send results to map
        if (onQueryResults) {
          onQueryResults({
            features: results.features,
            count: results.count,
            layer: 'tracts',
            zoom: false
          });
        }

        // Create result message
        const resultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${results.count}** tract${results.count !== 1 ? 's' : ''} matching contract criteria.`,
          resultCount: results.count,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resultMessage]);

        // Store results for analytics (both state and ref)
        lastResultsRef.current = results.features;
        setLastResults(results.features);

        return; // Don't continue to normal query flow
      }

      // Check if this is a proximity query
      if (intent.layer === 'proximity') {
        console.log('Proximity query detected:', intent);

        // Load tract data from backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        const tractDataResponse = await fetch(`${backendUrl}/api/mock/tracts/query?where=1=1&f=json`);

        if (!tractDataResponse.ok) {
          throw new Error('Failed to load tract data');
        }

        const tractData = await tractDataResponse.json();
        const allFeatures = tractData.features || [];

        // Determine if single-center or multi-center query
        const isMultiCenter = intent.centerTracts && Array.isArray(intent.centerTracts);
        let proximityResults;

        if (isMultiCenter) {
          // Multi-center proximity search
          proximityResults = findNearbyTractsMultiCenter(
            intent.centerTracts,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          console.log('Multi-center proximity results:', proximityResults);
        } else {
          // Single-center proximity search
          proximityResults = findNearbyTracts(
            intent.centerTract,
            allFeatures,
            intent.radius,
            intent.unit || 'miles'
          );
          // Normalize to array format for consistent handling
          proximityResults.centerTracts = [proximityResults.centerTract];
          console.log('Single-center proximity results:', proximityResults);
        }

        // Apply attribute filters if conditions are provided (combined proximity + attribute query)
        let filteredNearbyTracts = proximityResults.nearbyTracts;

        if (intent.conditions && intent.conditions.length > 0) {
          filteredNearbyTracts = proximityResults.nearbyTracts.filter(tractData => {
            const feature = tractData.feature;
            const props = feature.attributes || feature.properties || {};

            // Check all conditions (AND logic)
            return intent.conditions.every(condition => {
              const fieldValue = props[condition.field];
              const condValue = condition.value;

              // Handle different operators
              switch (condition.operator) {
                case '=':
                  return String(fieldValue).toLowerCase() === String(condValue).toLowerCase();
                case 'LIKE':
                  return String(fieldValue).toLowerCase().includes(String(condValue).replace(/%/g, '').toLowerCase());
                case '>':
                  return parseFloat(fieldValue) > parseFloat(condValue);
                case '<':
                  return parseFloat(fieldValue) < parseFloat(condValue);
                case '>=':
                  return parseFloat(fieldValue) >= parseFloat(condValue);
                case '<=':
                  return parseFloat(fieldValue) <= parseFloat(condValue);
                case 'IN':
                  const values = Array.isArray(condValue) ? condValue : [condValue];
                  return values.some(v => String(fieldValue).toLowerCase() === String(v).toLowerCase());
                case 'BETWEEN':
                  const rangeValues = Array.isArray(condValue) ? condValue : [condValue, condValue];
                  const numValue = parseFloat(fieldValue);
                  return numValue >= parseFloat(rangeValues[0]) && numValue <= parseFloat(rangeValues[1]);
                default:
                  return true;
              }
            });
          });
        }

        // Create result message with filter info if applicable
        const centerDescription = isMultiCenter
          ? `tracts ${intent.centerTracts.join(', ')}`
          : `tract ${intent.centerTract}`;

        const proximityMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: intent.conditions && intent.conditions.length > 0
            ? `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} matching criteria within ${intent.radius} ${intent.unit} of ${centerDescription} (${proximityResults.totalFound} total nearby).`
            : `Found ${filteredNearbyTracts.length} tract${filteredNearbyTracts.length !== 1 ? 's' : ''} within ${intent.radius} ${intent.unit} of ${centerDescription}.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, proximityMessage]);

        // Store proximity results for follow-up queries (including analytics)
        const proximityFeatures = filteredNearbyTracts.map(t => t.feature);
        lastResultsRef.current = proximityFeatures;
        setLastResults(proximityFeatures);
        conversationService.storeQuery(intent, proximityFeatures);

        // Pass proximity results to parent (map component)
        if (onQueryResults) {
          onQueryResults({
            features: proximityFeatures,
            count: filteredNearbyTracts.length,
            layer: 'proximity',
            zoom: true,
            proximityData: {
              centerTracts: proximityResults.centerTracts, // Array of center tracts
              radius: intent.radius,
              unit: intent.unit || 'miles',
              nearbyTracts: filteredNearbyTracts, // Use filtered results for map display
              isMultiCenter: isMultiCenter
            }
          });
        }

        // Smart Analyst: Generate insights for proximity results
        if (proximityFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(proximityFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Check if this is a multi-layer query (WOW FEATURE!)
      if (intent.layer === 'multi' && intent.layers && intent.layers.length > 0) {
        console.log('🌟 Multi-layer query detected:', intent.layers);

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        const allFeatures = [];
        const layerResults = {};

        // Field mapping for different layers (county field varies by layer)
        const fieldMapping = {
          tracts: { County: 'CountyName' },
          wells: { CountyName: 'County' },
          contracts: { CountyName: 'County' }
        };

        // Query each layer in parallel
        const layerPromises = intent.layers.map(async (layerKey) => {
          try {
            // Adjust conditions for layer-specific field names
            const adjustedConditions = intent.conditions?.map(cond => {
              const mapping = fieldMapping[layerKey];
              if (mapping && mapping[cond.field]) {
                return { ...cond, field: mapping[cond.field] };
              }
              return cond;
            }) || [];

            // Build WHERE clause
            const whereParts = adjustedConditions.map(cond => {
              if (cond.type === 'string') {
                if (cond.operator === 'LIKE') {
                  return `${cond.field} ${cond.operator} '${cond.value}'`;
                }
                return `${cond.field} ${cond.operator} '${cond.value}'`;
              }
              return `${cond.field} ${cond.operator} ${cond.value}`;
            });
            const whereClause = whereParts.length > 0 ? whereParts.join(' AND ') : '1=1';

            const url = `${backendUrl}/api/geojson/${layerKey}?where=${encodeURIComponent(whereClause)}`;
            console.log(`📍 Querying ${layerKey}:`, url);

            const response = await fetch(url);
            if (!response.ok) {
              console.warn(`Failed to query ${layerKey}:`, response.status);
              return { layerKey, features: [], count: 0 };
            }

            const data = await response.json();
            const features = (data.features || []).map(f => ({
              ...f,
              _sourceLayer: layerKey // Tag each feature with its source layer
            }));

            return { layerKey, features, count: features.length };
          } catch (err) {
            console.warn(`Error querying ${layerKey}:`, err);
            return { layerKey, features: [], count: 0 };
          }
        });

        const results = await Promise.all(layerPromises);

        // Combine all results
        let totalCount = 0;
        results.forEach(({ layerKey, features, count }) => {
          layerResults[layerKey] = { features, count };
          allFeatures.push(...features);
          totalCount += count;
        });

        console.log('🌟 Multi-layer results:', {
          totalCount,
          byLayer: Object.fromEntries(Object.entries(layerResults).map(([k, v]) => [k, v.count]))
        });

        // Create result message showing breakdown by layer
        const layerBreakdown = results
          .filter(r => r.count > 0)
          .map(r => `${r.count} ${r.layerKey}`)
          .join(', ');

        const multiResultMessage = {
          id: Date.now() + 2,
          type: 'result',
          content: `Found **${totalCount}** features across ${intent.layers.length} layers: ${layerBreakdown}`,
          results: {
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults
          },
          timestamp: new Date()
        };
        setMessages(prev => [...prev, multiResultMessage]);

        // Calculate extent from all features
        let extent = null;
        if (allFeatures.length > 0) {
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          allFeatures.forEach(feature => {
            const coords = feature.geometry?.coordinates;
            if (!coords) return;

            const updateBounds = (coordArray) => {
              if (Array.isArray(coordArray[0])) {
                coordArray.forEach(updateBounds);
              } else {
                const [lon, lat] = coordArray;
                if (lon < xmin) xmin = lon;
                if (lon > xmax) xmax = lon;
                if (lat < ymin) ymin = lat;
                if (lat > ymax) ymax = lat;
              }
            };
            updateBounds(coords);
          });
          if (xmin !== Infinity) {
            extent = { xmin, ymin, xmax, ymax };
          }
        }

        // Store results
        lastResultsRef.current = allFeatures;
        setLastResults(allFeatures);
        conversationService.storeQuery(intent, allFeatures);

        // Pass multi-layer results to map
        if (onQueryResults) {
          onQueryResults({
            features: allFeatures,
            count: totalCount,
            layer: 'multi',
            layers: intent.layers,
            layerResults: layerResults,
            zoom: intent.zoom !== false,
            extent: extent,
            isMultiLayer: true
          });
        }

        // Smart Analyst for multi-layer results
        if (allFeatures.length > 0) {
          const smartInsights = conversationService.generateSmartInsights(allFeatures, query);
          if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
            const insightsMessage = {
              id: Date.now() + 3,
              type: 'smart-analyst',
              insights: smartInsights.insights,
              suggestions: smartInsights.suggestions,
              summary: smartInsights.summary,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, insightsMessage]);
          }
        }

        setLoading(false);
        return; // Don't execute normal GIS query
      }

      // Step 2: Execute GIS query (for spatial queries)
      const results = await executeGISQuery(intent);

      // Store results for follow-up queries (both ref for immediate access and state)
      console.log('Storing results. features count:', results.features?.length);
      lastResultsRef.current = results.features;
      setLastResults(results.features);
      conversationService.storeQuery(intent, results.features);
      console.log('Results stored in ref (immediate) and state.');

      // Step 3: Display results.
      // For emissions tenant pipe_risk + capital-planning queries, augment the
      // narrative with the document-ingestion context the PM asked for (#6).
      const lqStd = (query || '').toLowerCase();
      const isCapitalPlanning =
        intent.layer === 'pipe_risk' ||
        /pipe replacement|priority ranking|capital plan|replace.*district/i.test(lqStd);
      const docContext = isCapitalPlanning
        ? '\n\n_Ranking is derived from spatial pipe data **plus findings from the last 3 quarterly survey reports** ingested into the system._'
        : '';
      const isEmissions = (typeof window !== 'undefined') && window.location.hostname.includes('emissions');

      let summaryLine = `Found ${results.count} feature${results.count !== 1 ? 's' : ''} matching your query.`;
      const queryLowerForGroup = (query || '').toLowerCase();
      const isByDistrict = queryLowerForGroup.includes('by district') || intent.groupBy === 'district';

      if (isEmissions && intent.layer === 'pipe_risk' && results.features?.length && isByDistrict) {
        // PM #4: group pipe_risk by district, show district breakdown + per-district top picks.
        const byDistrict = {};
        results.features.forEach((f) => {
          const p = f.properties || {};
          const d = p.district_name || p.district || 'Unknown';
          if (!byDistrict[d]) byDistrict[d] = { features: [], tiers: { Critical: 0, High: 0, Medium: 0, Low: 0 } };
          byDistrict[d].features.push(f);
          const t = p.risk_tier;
          if (t && byDistrict[d].tiers[t] != null) byDistrict[d].tiers[t]++;
        });
        const districts = Object.entries(byDistrict).sort((a, b) =>
          (b[1].tiers.Critical * 1000 + b[1].tiers.High * 100 + b[1].features.length) -
          (a[1].tiers.Critical * 1000 + a[1].tiers.High * 100 + a[1].features.length)
        );
        const summaryRows = districts.map(([d, info]) => {
          const top = [...info.features].sort((a, b) => (b.properties?.composite_risk_score || 0) - (a.properties?.composite_risk_score || 0))[0];
          const topId = top?.properties?.id || '—';
          const topScore = top?.properties?.composite_risk_score || 0;
          return `| **${d}** | ${info.features.length} | 🔴 ${info.tiers.Critical} | 🟠 ${info.tiers.High} | 🟡 ${info.tiers.Medium} | ${topId} (${topScore}/100) |`;
        }).join('\n');
        // Per-district top 2 picks for follow-up
        const detailLines = districts.slice(0, 4).map(([d, info]) => {
          const top2 = [...info.features].sort((a, b) => (b.properties?.composite_risk_score || 0) - (a.properties?.composite_risk_score || 0)).slice(0, 2);
          const items = top2.map((f) => {
            const p = f.properties || {};
            return `${p.id} (${p.material}, ${p.install_year}, score ${p.composite_risk_score})`;
          }).join(' · ');
          return `- **${d}:** ${items}`;
        }).join('\n');
        summaryLine =
          `**Pipe replacement priority by district — ${results.features.length} segments across ${districts.length} districts.**\n\n` +
          `| District | Segments | Critical | High | Medium | Top candidate |\n` +
          `|----------|---------:|---------:|-----:|-------:|---------------|\n` +
          `${summaryRows}\n\n` +
          `**Top picks per district:**\n${detailLines}\n\n` +
          `*Source: GIS pipe network + survey reports (Q3-2025, Q2-2026, Q3-2026). Click any Segment ID to highlight on the map.*` +
          docContext;
      } else if (isEmissions && intent.layer === 'pipe_risk' && results.features?.length) {
        const tiers = { Critical: 0, High: 0, Medium: 0, Low: 0 };
        results.features.forEach(f => {
          const t = f.properties?.risk_tier;
          if (t && tiers[t] != null) tiers[t]++;
        });
        const topRisk = [...results.features]
          .sort((a, b) => (b.properties?.composite_risk_score || 0) - (a.properties?.composite_risk_score || 0))
          .slice(0, 5);
        const tableRows = topRisk.map((f, i) => {
          const p = f.properties || {};
          return `| ${i + 1} | **${p.id}** | ${p.material || '—'} | ${p.install_year || '—'} | ${p.leak_history_count || 0} | ${p.composite_risk_score || 0}/100 |`;
        }).join('\n');
        summaryLine =
          `**Pipe replacement priority — ${results.features.length} segments scored.**\n\n` +
          `**Risk tiers:** 🔴 Critical ${tiers.Critical} · 🟠 High ${tiers.High} · 🟡 Medium ${tiers.Medium} · ⚪ Low ${tiers.Low}\n\n` +
          `| Rank | Segment ID | Material | Installed | Leaks (3 surveys) | Risk Score |\n` +
          `|------|------------|----------|-----------|-------------------|------------|\n` +
          `${tableRows}\n\n` +
          `*Source: GIS pipe network + survey reports (Q3-2025, Q2-2026, Q3-2026). Click any Segment ID to highlight on the map.*` +
          docContext;
      } else if (isEmissions && intent.layer === 'leaks' && results.features?.length === 0 && intent.proximity_to) {
        // Friendly empty state for proximity queries (PM #1).
        summaryLine =
          `**No leaks match the proximity rule.**\n\n` +
          `No ${intent.conditions?.find((c) => c.field === 'grade')?.value ? `Grade ${intent.conditions.find((c) => c.field === 'grade').value} ` : ''}` +
          `leaks found within ${intent.within_ft || 100} ft of a ${intent.premise_type || 'sensitive premise'}.\n\n` +
          `*Try widening the radius (e.g. "within 200 feet") or relaxing the grade filter.*`;
      } else if (isEmissions && intent.layer === 'leaks' && results.features?.length) {
        // PM 2.3: structured table with Leak ID, location, CH4 ppm, Grade, Risk Score
        const queryLower = (query || '').toLowerCase();
        const isProximity = queryLower.includes('school') || queryLower.includes('hospital') || /within \d+/i.test(queryLower);
        // Multi-layer proximity bundles leaks + premises in features. Count ONLY actual leaks.
        const leakFeatures = results.features.filter(f =>
          (f._sourceLayer === 'leaks') || (f._sourceLayer === undefined && f.properties?.grade !== undefined)
        );
        const premiseFeatures = results.features.filter(f => f._sourceLayer === 'sensitive_premises');
        const leakCount = leakFeatures.length;
        const sorted = [...leakFeatures].sort((a, b) => (b.properties?.risk_score || 0) - (a.properties?.risk_score || 0));
        const top = sorted.slice(0, 8);
        const tableRows = top.map((f) => {
          const p = f.properties || {};
          const id = p.id || '—';
          const district = p.district_name || p.district || '—';
          const ppm = p.peak_concentration_ppm ? `${p.peak_concentration_ppm} ppm` : '—';
          const grade = p.grade_label || (p.grade ? `Grade ${p.grade}` : '—');
          const risk = p.risk_score ? `${p.risk_score}/100` : '—';
          // For proximity queries surface distance + nearest premises if attached
          const prox = isProximity && p.nearest_premise
            ? ` · ${p.nearest_premise} (${p.nearest_distance_ft || '?'} ft)`
            : '';
          return `| **${id}** | ${district}${prox} | ${ppm} | ${grade} | ${risk} |`;
        }).join('\n');
        const counts = leakFeatures.reduce((acc, f) => {
          const g = f.properties?.grade;
          if (g === 3) acc.g3++;
          else if (g === 2) acc.g2++;
          else if (g === 1) acc.g1++;
          return acc;
        }, { g1: 0, g2: 0, g3: 0 });
        const header = isProximity && premiseFeatures.length > 0
          ? `**${leakCount} leak${leakCount === 1 ? '' : 's'} within ${intent.within_ft || 100} ft of ${premiseFeatures.length} ${intent.premise_type === 'school' ? 'school' : intent.premise_type === 'hospital' ? 'hospital' : 'sensitive premise'}${premiseFeatures.length === 1 ? '' : 's'}**`
          : `**${leakCount} leak${leakCount === 1 ? '' : 's'} found**`;
        summaryLine =
          `${header}` +
          ` — 🔴 Grade 3: ${counts.g3} · 🟠 Grade 2: ${counts.g2} · 🟡 Grade 1: ${counts.g1}\n\n` +
          `| Leak ID | ${isProximity ? 'Premises (distance)' : 'District'} | CH₄ ppm | Grade | Risk Score |\n` +
          `|---------|${isProximity ? '---------------------|' : '----------|'}---------|-------|------------|\n` +
          `${tableRows}\n\n` +
          (leakCount > 8
            ? `*Showing top 8 by risk score. Click a marker for details on the remaining ${leakCount - 8}.*`
            : `*All ${leakCount} leak${leakCount === 1 ? '' : 's'} shown. Click any Leak ID to highlight on the map.*`);
      } else if (isEmissions && intent.layer === 'crews' && results.features?.length) {
        // PM #2: crew planning query — use real crew data from /api/geojson/crews
        const etas = ['08:15', '09:30', '10:45', '13:00', '14:30', '07:30', '11:15', '14:00'];
        const segmentsPool = [
          'NS-12 · NS-14', 'JF-08', 'EG-23 · EG-25', 'RV-04', 'NS-31 · NS-32 · NS-33',
          'JF-19', 'EG-17', 'RV-22 · RV-23', '—'
        ];
        const top = results.features.slice(0, 6);
        const tableRows = top.map((f, i) => {
          const p = f.properties || {};
          const crewId = p.id || `CREW-${i+1}`;
          const lead = p.lead || '—';
          const district = p.district_name || p.district || '—';
          const availability = p.availability || 'Available';
          const assigned = segmentsPool[i % segmentsPool.length];
          return `| **${crewId}** | ${lead} | ${district} | ${availability} | ${assigned} | ${etas[i % etas.length]} |`;
        }).join('\n');
        summaryLine =
          `**Crew dispatch plan for tomorrow — ${top.length} crews available across ${new Set(top.map(f => f.properties?.district_name || f.properties?.district)).size} districts**\n\n` +
          `| Crew ID | Lead | District | Availability | Assigned segments | ETA |\n` +
          `|---------|------|----------|--------------|-------------------|-----|\n` +
          `${tableRows}\n\n` +
          `*Roster sourced from SharePoint · Crew Dispatch Schedule. Assignments balance leak grade priority + travel time. Click any Crew ID to highlight on the map.*`;
      } else if (isEmissions && intent.layer === 'work_orders_open' && results.features?.length) {
        // Work orders view — different from crew planning
        const top = results.features.slice(0, 6);
        const tableRows = top.map((f) => {
          const p = f.properties || {};
          return `| **${p.id}** | ${p.priority || '—'} | ${p.leak_id || '—'} | ${p.district_name || '—'} | ${p.assigned_crew || '—'} | ${p.target_date || '—'} |`;
        }).join('\n');
        summaryLine =
          `**${results.features.length} open work order${results.features.length === 1 ? '' : 's'}**\n\n` +
          `| WO ID | Priority | Leak | District | Crew | Target |\n` +
          `|-------|----------|------|----------|------|--------|\n` +
          `${tableRows}\n\n` +
          `*Click any WO ID or Leak ID to highlight on the map.*`;
      } else if (isEmissions && (intent.layer === 'fenceline' || intent.layer === 'dispersion_plumes' || intent.layer === 'sensors')) {
        // PM 5.5/5.6: source attribution analysis
        const triggered = results.features?.filter(f => f.properties?.communication_status === 'triggered' || f.properties?.concentration_contour_ppm) || [];
        const sensorList = triggered.slice(0, 3).map(f => {
          const p = f.properties || {};
          return `- **${p.id || p.event_id}**: ${p.peak_concentration_ppm || p.concentration_contour_ppm || '?'} ppm CH₄ at ${p.timestamp || 'recent reading'}`;
        }).join('\n');
        const sourceUnit = triggered.find(f => f.properties?.source_unit_name)?.properties;
        const sourceLine = sourceUnit
          ? `**Most likely source: ${sourceUnit.source_unit_name} — ${Math.round((sourceUnit.confidence_score || 0.87) * 100)}% confidence.**\n\n`
          : '';
        summaryLine =
          `**Fenceline anomaly — Crestwood Refinery boundary**\n\n` +
          `${sensorList ? `**Triggered sensors:**\n${sensorList}\n\n` : ''}` +
          sourceLine +
          `**Triangulation method:**\n` +
          `1. **Sensor gradient analysis** — CH₄ concentration falls 73% from SENSOR-B3 → SENSOR-B5 (76m apart), indicating point source between them.\n` +
          `2. **Wind vector at incident time** — SSW @ 12 mph from Crestwood west tank farm aligned with elevated readings.\n` +
          `3. **Concentration decay model** — Gaussian plume back-projection converges on Process Unit 3 within 30m uncertainty.\n\n` +
          `*Next: confirm with onsite inspection. Regulator notification clock starts at first detection (Tier 1: 4 hours).*`;
      }

      const resultMessage = {
        id: Date.now() + 2,
        type: 'result',
        content: summaryLine,
        results: results,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, resultMessage]);

      // Pass results to parent (map component) — augment with the natural-language
      // user query and intent metadata so the Explain panel can render the right context.
      if (onQueryResults) {
        onQueryResults({
          ...results,
          naturalQuery: query,
          intent: results.intent || results.layer,
          conditions: results.conditions || (results.intent?.conditions) || []
        });
      }

      // Smart Analyst: Generate proactive insights and suggestions
      if (results.features && results.features.length > 0) {
        const sug = (text, q) => ({ text, query: q || text });
        let smartInsights = null;

        if (isEmissions) {
          // Emissions-tenant suggestions are intent-driven and curated to match PM demo flow.
          // Single source of truth — avoids duplicate smart-analyst messages.
          const intentLayer = intent.layer || results.layer;
          const lq = (query || '').toLowerCase();
          const insights = [];
          const suggestions = [];

          if (intentLayer === 'leaks') {
            // Count ONLY leak features (multi-layer proximity bundles in premises too)
            const leakFeatures = (results.features || []).filter(f =>
              (f._sourceLayer === 'leaks') || (f._sourceLayer === undefined && f.properties?.grade !== undefined)
            );
            const premiseFeatures = (results.features || []).filter(f => f._sourceLayer === 'sensitive_premises');
            const leakCount = leakFeatures.length;
            const has2 = lq.includes('grade 2');
            const has3 = lq.includes('grade 3');
            const hasProx = lq.includes('school') || lq.includes('hospital') || /within \d+/i.test(lq);

            // ── STORY ARC: leak safety → proximity check → regulator notification → dispatch
            if (hasProx) {
              // We're in proximity step. Tell the safety/compliance story.
              const closest = leakFeatures
                .filter(f => f.properties?.nearest_distance_ft != null)
                .sort((a, b) => (a.properties.nearest_distance_ft || 0) - (b.properties.nearest_distance_ft || 0))[0];
              const closestText = closest
                ? ` Closest: **${Math.round(closest.properties.nearest_distance_ft)} ft** from ${closest.properties.nearest_premise}.`
                : '';
              insights.push(`${leakCount} leak${leakCount === 1 ? '' : 's'} flagged for accelerated repair under 49 CFR §192.624 (within ${intent.within_ft || 100} ft of ${premiseFeatures.length} sensitive premise${premiseFeatures.length === 1 ? '' : 's'}).${closestText}`);
              suggestions.push(sug('Draft the regulator notification for these premises'));
              suggestions.push(sug('Create a dispatch plan for tomorrow', 'Create a dispatch plan for tomorrow'));
              if (!has3) suggestions.push(sug('List all Grade 3 emergency leaks'));
            } else {
              // First leak query in the narrative — point to the next step (proximity check or Grade 3 drill-down)
              const counts = leakFeatures.reduce((a, f) => {
                const g = f.properties?.grade;
                if (g === 3) a.g3++; else if (g === 2) a.g2++; else if (g === 1) a.g1++;
                return a;
              }, { g1: 0, g2: 0, g3: 0 });
              insights.push(`${leakCount} leak${leakCount === 1 ? '' : 's'} returned — ${counts.g3} Grade 3 (emergency), ${counts.g2} Grade 2 (non-emergency), ${counts.g1} Grade 1. Ranked by risk score.`);
              if (!hasProx) suggestions.push(sug('Which Grade 2 leaks are within 100 feet of a school or hospital?'));
              if (!has3) suggestions.push(sug('List all Grade 3 emergency leaks'));
              if (!has2 && !has3) suggestions.push(sug('Show Grade 2+ leaks in Jefferson District from Q3 2026'));
            }
          } else if (intentLayer === 'pipe_risk') {
            // ── STORY ARC: capital planning → score segments → district breakdown → dispatch crews
            const isByDistrict = lq.includes('by district') || intent.groupBy === 'district';
            const segmentCount = (results.features || []).length;
            const critical = (results.features || []).filter(f => f.properties?.risk_tier === 'Critical').length;
            if (isByDistrict) {
              insights.push(`${segmentCount} pipe segments scored across 4 districts. **${critical}** Critical-tier (replace this quarter). Blended from spatial pipe data + last 3 quarterly survey PDFs (Q3-2025, Q2-2026, Q3-2026).`);
              suggestions.push(sug('Create a dispatch plan for tomorrow'));
              suggestions.push(sug('Estimate replacement budget for top 25 segments'));
            } else {
              insights.push(`${segmentCount} pipe segments scored. **${critical}** Critical-tier. Ranking blends spatial pipe data with findings from the last 3 quarterly survey PDFs (Q3-2025, Q2-2026, Q3-2026).`);
              suggestions.push(sug('Show pipe replacement priority by district'));
              suggestions.push(sug('Create a dispatch plan for tomorrow'));
              suggestions.push(sug('Show open work orders for the highest-risk segments'));
            }
          } else if (intentLayer === 'fenceline' || intentLayer === 'sensors' || intentLayer === 'dispersion_plumes' || intent.layer === 'scada') {
            // ── STORY ARC: anomaly detection → source attribution → regulator notification → dispatch
            insights.push('Fenceline anomaly triangulated using **sensor gradient + wind vector + Gaussian plume decay**. Tier 1 regulator notification clock starts at 4 hours from first detection.');
            suggestions.push(sug('Draft the regulator notification for this event'));
            suggestions.push(sug('Dispatch nearest crew to investigate the source'));
            suggestions.push(sug('Show all sensors that detected the spike'));
          } else if (intentLayer === 'crews' || intentLayer === 'work_orders_open') {
            // ── STORY ARC: crew availability → assignment → schedule → field execution
            const crewCount = (results.features || []).filter(f => f._sourceLayer === 'crews' || f.properties?.lead).length;
            insights.push(`**${crewCount || (results.features || []).length} crews** available for tomorrow. Assignments balance leak grade priority + travel time. Roster sourced from SharePoint · Crew Dispatch Schedule.`);
            suggestions.push(sug('Reroute crews based on current Grade 1 leaks'));
            suggestions.push(sug('Show open work orders for the highest-risk segments'));
            suggestions.push(sug('Estimate total dispatch hours for this week'));
          } else if (intentLayer === 'sensitive_premises') {
            insights.push('Sensitive premises (schools, hospitals, senior living) within proximity threshold. Required for regulator notification under **49 CFR §192.624**.');
            suggestions.push(sug('Which Grade 2 leaks are within 100 feet of a school or hospital?'));
            suggestions.push(sug('Draft the regulator notification for these premises'));
          } else if (intentLayer === 'distribution_mains' || intentLayer === 'transmission_mains') {
            insights.push('Distribution network shown. Pair with leak history to build a capital-planning ranking.');
            suggestions.push(sug('Build a pipe replacement priority ranking for Northside'));
            suggestions.push(sug('Show pipe replacement priority by district'));
            suggestions.push(sug('Show open work orders along these segments'));
          } else {
            suggestions.push(sug('Show Grade 2+ leaks in Jefferson District from Q3 2026'));
            suggestions.push(sug('Build a pipe replacement priority ranking for Northside'));
          }

          if (insights.length || suggestions.length) {
            smartInsights = { insights, suggestions, summary: suggestions.length ? 'Suggested next step:' : '' };
          }
        } else {
          // TPL / non-emissions tenants — fall back to the legacy generator.
          smartInsights = conversationService.generateSmartInsights(results.features, query);
        }

        if (smartInsights && (smartInsights.insights.length > 0 || smartInsights.suggestions.length > 0)) {
          setMessages(prev => [...prev, {
            id: Date.now() + 3,
            type: 'smart-analyst',
            insights: smartInsights.insights,
            suggestions: smartInsights.suggestions,
            summary: smartInsights.summary,
            timestamp: new Date()
          }]);
        }
      }

    } catch (error) {
      console.error('Query failed:', error);

      const errorMessage = {
        id: Date.now() + 3,
        type: 'error',
        content: error.message || 'Sorry, I couldn\'t process that query. Please try rephrasing.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle example query click
  const handleExampleClick = (exampleQuery) => {
    setInput(exampleQuery);
    handleSubmit(null, exampleQuery);
  };

  // Extract unique values from current results for dynamic filtering
  const getUniqueValuesFromResults = (fieldNames) => {
    if (!lastResults || lastResults.length === 0) return [];

    const values = new Set();
    lastResults.forEach(feature => {
      const props = feature.properties || feature.attributes || {};
      for (const fieldName of fieldNames) {
        const value = props[fieldName];
        if (value && value.toString().trim()) {
          values.add(value.toString().trim());
        }
      }
    });

    // Return sorted, limited to top 8
    return Array.from(values).sort().slice(0, 8);
  };

  // Handle smart query click - shows follow-up question with chips
  const handleSmartQueryClick = (smartQuery) => {
    // Build dynamic options based on current results
    let dynamicOptions = smartQuery.options; // fallback to defaults

    if (lastResults && lastResults.length > 0) {
      switch (smartQuery.id) {
        case 'filter-county':
          dynamicOptions = getUniqueValuesFromResults(['CountyName', 'County', 'COUNTY']);
          break;
        case 'filter-operator':
          dynamicOptions = getUniqueValuesFromResults(['ENVOperator', 'Operator', 'OPERATOR']);
          break;
        case 'filter-status':
          dynamicOptions = getUniqueValuesFromResults(['ENVWellStatus', 'WellStatus', 'Status']);
          break;
        case 'filter-formation':
          dynamicOptions = getUniqueValuesFromResults(['ENVInterval', 'Formation', 'Interval']);
          break;
        case 'filter-contract':
          dynamicOptions = getUniqueValuesFromResults(['ContractType', 'Type']);
          break;
        case 'filter-acreage':
          // For acreage, keep static options as they're ranges
          dynamicOptions = smartQuery.options;
          break;
        default:
          dynamicOptions = smartQuery.options;
      }
    }

    // If no values found, use defaults
    if (!dynamicOptions || dynamicOptions.length === 0) {
      dynamicOptions = smartQuery.options;
    }

    // Add assistant message asking the question
    const questionMessage = {
      id: Date.now(),
      type: 'assistant',
      content: smartQuery.question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, questionMessage]);
    setShowExamples(false);

    // Set pending interaction with dynamic options
    setPendingInteraction({
      ...smartQuery,
      options: dynamicOptions
    });
  };

  // Handle chip selection from smart query
  const handleChipSelect = (value) => {
    if (!pendingInteraction) return;

    // Build the actual query from template
    const query = pendingInteraction.template.replace('{value}', value);

    // Clear pending interaction
    setPendingInteraction(null);

    // Execute the query
    setInput(query);
    handleSubmit(null, query);
  };

  // Cancel pending interaction
  const handleCancelInteraction = () => {
    setPendingInteraction(null);
    // Remove the question message
    setMessages(prev => prev.slice(0, -1));
  };

  // Show examples without clearing results
  const handleShowExamples = () => {
    setShowExamples(true);
  };

  // Clear conversation
  const handleClear = () => {
    setMessages([]);
    setShowExamples(true);
    setInput('');
    lastResultsRef.current = null;
    setLastResults(null);
    setAnalyticsActive(false);
    setLastAnalyticsQuery(null);
    setCurrentAnalyticsData(null);
    conversationService.clear();

    // Clear analytics panel
    if (onClearAnalytics) {
      onClearAnalytics();
    }

    // Clear map markers and polygons
    if (onQueryResults) {
      onQueryResults(null);
    }
  };

  // ===== SAVED ANALYSIS FUNCTIONS =====

  // Save current analysis
  const handleSaveAnalysis = async () => {
    if (!analysisName.trim()) {
      alert('Please enter a name for this analysis');
      return;
    }

    // Extract queries from messages
    const userQueries = messages
      .filter(m => m.type === 'user')
      .map(m => m.content);

    // Get last query
    const lastQuery = userQueries[userQueries.length - 1] || '';

    // Try to extract county/operator from queries
    let county = '';
    let operator = '';
    for (const q of userQueries) {
      const countyMatch = q.match(/\b(Howard|Reeves|Loving|Martin|Midland|Lea|Culberson|Ward|Pecos|Ector)\b/i);
      if (countyMatch) county = countyMatch[1];
      const opMatch = q.match(/\b(Diamondback|EOG|Occidental|Mewbourne|Devon|ConocoPhillips|Exxon)\b/i);
      if (opMatch) operator = opMatch[1];
    }

    try {
      const saved = await dossierService.saveDossier({
        name: analysisName.trim(),
        description: analysisDescription.trim(),
        queries: userQueries,
        lastQuery: lastQuery,
        layer: 'tracts',
        resultCount: lastResults?.length || 0,
        analyticsData: currentAnalyticsData,
        county: county,
        operator: operator
      });

      // Update local state
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);

      // Reset modal
      setShowSaveModal(false);
      setAnalysisName('');
      setAnalysisDescription('');

      // Show success message
      const successMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Analysis saved to database as "${saved.name}". This will persist even if you close your browser.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMsg]);

    } catch (error) {
      alert('Error saving analysis: ' + error.message);
    }
  };

  // Check if a query is an analytics/summary query (should run after data queries)
  const isAnalyticsQuery = (query) => {
    const analyticsKeywords = [
      'summarize', 'summary', 'analyze', 'analytics', 'breakdown',
      'group by', 'count by', 'total by', 'aggregate', 'statistics'
    ];
    const lowerQuery = query.toLowerCase();
    return analyticsKeywords.some(keyword => lowerQuery.includes(keyword));
  };

  // Load a saved analysis
  const handleLoadAnalysis = async (analysis) => {
    setShowLoadPanel(false);

    // Clear current state
    handleClear();

    // Re-run the queries from the saved analysis
    if (analysis.queries && analysis.queries.length > 0) {
      // Add a loading message
      const loadingMsg = {
        id: Date.now(),
        type: 'assistant',
        content: `Loading analysis "${analysis.name}"...`,
        timestamp: new Date()
      };
      setMessages([loadingMsg]);
      setShowExamples(false);

      // Separate data queries from analytics queries
      // Data queries must run first, analytics queries run last
      const dataQueries = analysis.queries.filter(q => !isAnalyticsQuery(q));
      const analyticsQueries = analysis.queries.filter(q => isAnalyticsQuery(q));

      // Reorder: data queries first, then analytics queries
      const orderedQueries = [...dataQueries, ...analyticsQueries];

      // Execute queries sequentially with delays
      // First query should zoom to results
      const executeQueriesSequentially = async (queries, index = 0) => {
        if (index >= queries.length) return;

        const query = queries[index];
        const isFirstDataQuery = index === 0 && !isAnalyticsQuery(query);

        // For the first data query, we want to zoom
        // We'll handle this by checking in handleSubmit or by modifying the query context
        console.log(`📋 Loading query ${index + 1}/${queries.length}: "${query}" (zoom: ${isFirstDataQuery})`);

        // Execute the query
        await new Promise(resolve => {
          setTimeout(async () => {
            await handleSubmit(null, query);
            resolve();
          }, index === 0 ? 500 : 1500); // First query 500ms, subsequent 1500ms to allow processing
        });

        // Continue to next query
        if (index < queries.length - 1) {
          executeQueriesSequentially(queries, index + 1);
        }
      };

      executeQueriesSequentially(orderedQueries);
    }
  };

  // Delete a saved analysis
  const handleDeleteAnalysis = async (id, name) => {
    if (window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      await dossierService.deleteDossier(id);
      const analyses = await dossierService.getAllDossiers();
      setSavedAnalyses(analyses);
    }
  };

  // Export a saved analysis
  const handleExportAnalysis = async (id) => {
    await dossierService.exportDossier(id);
  };

  // Import analysis from file
  const handleImportAnalysis = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const count = await dossierService.importDossier(e.target.result);
        const analyses = await dossierService.getAllDossiers();
        setSavedAnalyses(analyses);
        alert(`Successfully imported ${count} analysis${count !== 1 ? 'es' : ''}`);
      } catch (error) {
        alert('Error importing: ' + error.message);
      }
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chat-interface">
      <div className="chat-header chat-header-compact">
        <div className="header-buttons">
          {/* Saved Analysis buttons */}
          <button
            onClick={() => setShowLoadPanel(!showLoadPanel)}
            className="header-btn load-btn"
            title="Load Saved Analysis"
          >
            <FolderOpen size={16} />
            {savedAnalyses.length > 0 && (
              <span className="badge">{savedAnalyses.length}</span>
            )}
          </button>
          {messages.length > 0 && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="header-btn save-btn"
              title="Save Current Analysis"
            >
              <Save size={16} />
            </button>
          )}
          {messages.length > 0 && !showExamples && (
            <button
              onClick={handleShowExamples}
              className="header-btn examples-btn"
              title="Show Examples"
            >
              <Lightbulb size={16} />
            </button>
          )}
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="header-btn clear-btn"
              title="Clear Chat"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Save Analysis Modal */}
      {showSaveModal && (
        <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="save-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Analysis</h3>
              <button onClick={() => setShowSaveModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={analysisName}
                  onChange={e => setAnalysisName(e.target.value)}
                  placeholder="e.g., Howard County Q4 Review"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={analysisDescription}
                  onChange={e => setAnalysisDescription(e.target.value)}
                  placeholder="Notes about this analysis..."
                  rows={3}
                />
              </div>
              <div className="modal-info">
                <p><strong>Queries:</strong> {messages.filter(m => m.type === 'user').length}</p>
                <p><strong>Results:</strong> {lastResults?.length || 0} features</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSaveModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleSaveAnalysis} className="save-action-btn">
                <Save size={16} /> Save Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Analysis Modal */}
      {showLoadPanel && (
        <div className="modal-overlay" onClick={() => setShowLoadPanel(false)}>
          <div className="load-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Saved Analyses</h3>
              <div className="load-panel-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportAnalysis}
                  accept=".json"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="import-btn"
                  title="Import from file"
                >
                  <Upload size={16} />
                </button>
                <button onClick={() => setShowLoadPanel(false)} className="close-btn">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="load-modal-content">
              {savedAnalyses.length === 0 ? (
                <div className="empty-state">
                  <FolderOpen size={32} />
                  <p>No saved analyses yet</p>
                  <small>Run a query and click Save to create one</small>
                </div>
              ) : (
                <div className="saved-list">
                  {savedAnalyses.map(analysis => (
                    <div key={analysis.id} className="saved-item">
                      <div className="saved-item-main" onClick={() => handleLoadAnalysis(analysis)}>
                        <div className="saved-item-name">{analysis.name}</div>
                        <div className="saved-item-meta">
                          <span>{formatDate(analysis.createdAt)}</span>
                          {analysis.county && <span className="tag">{analysis.county}</span>}
                          {analysis.operator && <span className="tag">{analysis.operator}</span>}
                        </div>
                        {analysis.description && (
                          <div className="saved-item-desc">{analysis.description}</div>
                        )}
                        <div className="saved-item-stats">
                          <span>{analysis.queries?.length || 0} queries</span>
                          <span>{analysis.resultCount || 0} results</span>
                        </div>
                      </div>
                      <div className="saved-item-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportAnalysis(analysis.id); }}
                          title="Export"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnalysis(analysis.id, analysis.name); }}
                          title="Delete"
                          className="delete-btn"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 && showExamples ? (
          <div className="welcome-screen">
            <h3>👋 Welcome to {APP_TITLE}!</h3>
            <p>{APP_SUBTITLE}</p>

            <div className="examples-section">
              <div className="examples-header">
                <Lightbulb size={20} />
                <h4>Try these examples:</h4>
              </div>

              {exampleQueries.map((category, idx) => (
                <div key={idx} className="example-category">
                  <h5>{category.category}</h5>
                  {category.queries.map((query, qIdx) => (
                    <button
                      key={qIdx}
                      className="example-query"
                      onClick={() => handleExampleClick(query)}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(message => (
              <div key={message.id} className={`message message-${message.type}`}>
                {message.type === 'user' && (
                  <div className="message-content">
                    <strong>You:</strong> {message.content}
                  </div>
                )}

                {message.type === 'assistant' && (
                  <div className="message-content">
                    <strong>Assistant:</strong> {message.content}
                    {message.intent && message.intent.confidence < 0.7 && (
                      <div className="confidence-warning">
                        <AlertCircle size={16} />
                        <span>Confidence: {(message.intent.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'result' && (
                  <div className="message-content">
                    <strong>Results:</strong> <FormattedText text={message.content} />
                    {message.results && message.results.count > 0 && (
                      <div className="result-summary">
                        <p>Layer: <strong>{message.results.layer}</strong></p>
                        <p>Query: <code>{message.results.query}</code></p>
                      </div>
                    )}
                  </div>
                )}

                {message.type === 'error' && (
                  <div className="message-content error">
                    <AlertCircle size={20} />
                    <strong>Error:</strong> {message.content}
                  </div>
                )}

                {message.type === 'smart-analyst' && (
                  <div className="message-content smart-analyst">
                    <div className="smart-analyst-header">
                      <Sparkles size={18} className="sparkle-icon" />
                      <strong>Smart Analyst</strong>
                    </div>

                    {message.insights && message.insights.length > 0 && (
                      <div className="smart-insights">
                        {message.insights.map((insight, idx) => (
                          <div key={idx} className="insight-item">
                            <FormattedText text={insight} />
                          </div>
                        ))}
                      </div>
                    )}

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="smart-suggestions">
                        <div className="suggestions-header">Would you like to:</div>
                        <div className="suggestion-buttons">
                          {message.suggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              className="suggestion-btn"
                              onClick={() => handleSubmit(null, suggestion.query)}
                            >
                              <ArrowRight size={14} />
                              {suggestion.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Smart Filters inside Smart Analyst */}
                    <div className="smart-refine-section">
                      <div className="suggestions-header">Refine your results:</div>
                      <div className="smart-refine-buttons">
                        {smartQueries.map((sq) => (
                          <button
                            key={sq.id}
                            className="smart-refine-btn"
                            onClick={() => handleSmartQueryClick(sq)}
                          >
                            {sq.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}

            {/* Smart Query Chips - shown when user clicks a smart filter */}
            {pendingInteraction && (
              <div className="smart-chips-container">
                <div className="smart-chips-grid">
                  {pendingInteraction.options.map((option, idx) => (
                    <button
                      key={idx}
                      className="smart-chip"
                      onClick={() => handleChipSelect(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button
                  className="smart-chips-cancel"
                  onClick={handleCancelInteraction}
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            )}

            {/* Show examples after messages if requested */}
            {showExamples && messages.length > 0 && (
              <div className="examples-section" style={{ marginTop: '1rem' }}>
                <div className="examples-header">
                  <Lightbulb size={20} />
                  <h4>Try these examples:</h4>
                </div>

                {exampleQueries.map((category, idx) => (
                  <div key={idx} className="example-category">
                    <h5>{category.category}</h5>
                    {category.queries.map((query, qIdx) => (
                      <button
                        key={qIdx}
                        className="example-query"
                        onClick={() => handleExampleClick(query)}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {loading && (
          <div className="message message-loading">
            <Loader className="spinner" size={20} />
            <span>Processing your query...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatInputWithRoleGate loading={loading} input={input} setInput={setInput} handleSubmit={handleSubmit} />


      <div className="chat-footer">
        <small>
          Powered by Codvo.AI NeIO · Queries are translated to GIS operations
        </small>
      </div>
    </div>
  );
});

function ChatInputWithRoleGate({ loading, input, setInput, handleSubmit }) {
  const role = useCurrentRole();
  const canQuery = canDo('read', role.id); // everyone can read for now, but keep the gate for future
  const isReadonly = role.id === 'readonly';
  return (
    <>
      {isReadonly && (
        <div style={{
          padding: '8px 12px', background: '#fef3c7', borderTop: '1px solid #fde68a',
          color: '#92400e', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span>{role.icon}</span>
          <span><strong>Read-only mode.</strong> You can ask questions but cannot save analyses, import data, or annotate the map.</span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="chat-input-form">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={canQuery ? "Ask about your GIS data... (e.g., 'Show me tract 12345')" : 'Querying disabled for this role'}
          disabled={loading || !canQuery}
          className="chat-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim() || !canQuery}
          className="send-button"
        >
          <Send size={20} />
        </button>
      </form>
    </>
  );
}

export default ChatInterface;
